import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { Observable } from 'rxjs';

import { BaseHttpReqRespService } from '../../../modules/fwcore/services';
import {
  RequestHelpContext, RequestHelpGlossaryScope, RequestHelpItem, RequestHelpSearch,
  RequestHelpTree, RequestHelpType,
  ResponseHelpContext, ResponseHelpGlossaryScope, ResponseHelpItem, ResponseHelpSearch,
  ResponseHelpTree, ResponseHelpType
} from '../models';

/**
 * HTTP access to the in-product Help endpoints. No state - callers hold their own.
 *
 * Every endpoint here is reader-facing and returns the reader projection, which has no
 * authorship. There is deliberately no admin call: the API has no admin endpoint yet,
 * and when it arrives in Slice 3 it belongs behind the page-access guard rather than
 * alongside these.
 */
@Injectable({
  providedIn: 'root'
})
export class HelpApiService extends BaseHttpReqRespService {

  // ----------------- Constants ---------------------------------------------

  readonly endPointHelpType: string = 'help/type';
  readonly endPointHelpTree: string = 'help/tree';
  readonly endPointHelpItem: string = 'help/item';
  readonly endPointHelpContext: string = 'help/context';
  readonly endPointHelpSearch: string = 'help/search';
  readonly endPointHelpGlossaryScope: string = 'help/glossary-scope';

  // ----------------- Constructor -------------------------------------------

  constructor(
    http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,
    oktaWrapper: OktaWrapperService,
    impersonation: ImpersonationService
  ) {
    super(http, config, impersonation, oktaWrapper);
  }

  // ----------------- Reads --------------------------------------------------

  /**
   * Gets the Help node-kind lookup - 'def', 'nav' and 'struct'.
   */
  public helpTypeGet$(req: RequestHelpType): Observable<ResponseHelpType> {

    return this.getData$<RequestHelpType, ResponseHelpType>(req, this.endPointHelpType);
  }

  /**
   * Gets the Help hierarchy that drives the Help navigation.
   *
   * Never returns article bodies, so this is cheap enough to call on every Help open.
   * Pass maxDepth 0 when only the section roots are wanted, which is what the left-hand
   * navigation needs.
   */
  public helpTreeGet$(req: RequestHelpTree): Observable<ResponseHelpTree> {

    return this.getData$<RequestHelpTree, ResponseHelpTree>(req, this.endPointHelpTree);
  }

  /**
   * Gets Help articles with their bodies, addressed by ids or by parent.
   *
   * Request a single id to get the ancestor trail back with it; a multi-item request
   * returns no trail, which is expected rather than an error.
   */
  public helpItemGet$(req: RequestHelpItem): Observable<ResponseHelpItem> {

    return this.getData$<RequestHelpItem, ResponseHelpItem>(req, this.endPointHelpItem);
  }

  /**
   * Resolves which Help article answers a given screen context.
   *
   * Resolution falls back rather than failing, but it can still come back with no
   * article when there is no content to fall back to - callers must handle that.
   */
  public helpContextGet$(req: RequestHelpContext): Observable<ResponseHelpContext> {

    return this.getData$<RequestHelpContext, ResponseHelpContext>(req, this.endPointHelpContext);
  }

  /**
   * Searches Help, either within one section or across all of it.
   *
   * Omit sectionId to widen the search to all of Help - those are the two scopes the
   * search control offers.
   */
  public helpSearchGet$(req: RequestHelpSearch): Observable<ResponseHelpSearch> {

    return this.getData$<RequestHelpSearch, ResponseHelpSearch>(req, this.endPointHelpSearch);
  }

  /**
   * Gets which glossary scope each Help item shows, if any.
   *
   * Ask for the whole set once and join it to the tree, rather than calling per page:
   * it is two dozen rows, and a call per page would be one request per click.
   *
   * The mapping does NOT arrive on the tree or on an article. Those result sets are
   * read positionally server-side, so appending a column to them is a two-place change
   * that fails silently when the two drift - see HelpGlossaryScope.
   *
   * An empty result is normal, not a failure: nothing is mapped until an author maps
   * it, and an unmapped page offers no Glossary tab.
   */
  public helpGlossaryScopeGet$(req: RequestHelpGlossaryScope): Observable<ResponseHelpGlossaryScope> {

    return this.getData$<RequestHelpGlossaryScope, ResponseHelpGlossaryScope>(
      req, this.endPointHelpGlossaryScope);
  }
}
